import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Shield, ShieldCheck, Zap, Lock, Globe, Check, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Security() {
  const [packages, setPackages] = useState({});
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [inquiryData, setInquiryData] = useState({
    website_url: '',
    contact_email: '',
    description: ''
  });
  
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/security/packages`);
      setPackages(response.data);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    }
  };

  const handlePurchase = async (packageId) => {
    if (!isAuthenticated) {
      toast.info('Please sign in to purchase security services');
      navigate('/login');
      return;
    }

    setSelectedPackage(packageId);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      toast.error('Failed to initiate checkout. Please try again.');
    } finally {
      setLoading(false);
      setSelectedPackage(null);
    }
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/security/inquiry`, {
        ...inquiryData,
        service_type: 'custom'
      });
      toast.success('Inquiry submitted successfully! We\'ll contact you soon.');
      setShowInquiryForm(false);
      setInquiryData({ website_url: '', contact_email: '', description: '' });
    } catch (error) {
      toast.error('Failed to submit inquiry');
    } finally {
      setLoading(false);
    }
  };

  const packageList = [
    {
      id: 'basic',
      icon: Shield,
      popular: false,
      color: 'blue'
    },
    {
      id: 'professional',
      icon: ShieldCheck,
      popular: true,
      color: 'green'
    },
    {
      id: 'enterprise',
      icon: Lock,
      popular: false,
      color: 'purple'
    }
  ];

  const features = [
    { icon: Zap, title: 'Fast Response', description: 'Quick turnaround on all security assessments' },
    { icon: Lock, title: 'Full Protection', description: 'Comprehensive security coverage' },
    { icon: Globe, title: '24/7 Monitoring', description: 'Round-the-clock threat detection' }
  ];

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/20 via-gray-950 to-gray-950 -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-green-500/20 text-green-400 border-green-500/30">
            <Shield className="w-3 h-3 mr-1" />
            Operation Security
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 font-['Outfit']">
            Security <span className="text-gradient">Services</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Protect your digital assets from intrusions. Professional security audits and protection services.
          </p>
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {features.map((feature, index) => (
            <Card key={index} className="bg-gray-900/30 border-white/5 text-center">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-white font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-500 text-sm">{feature.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {packageList.map((pkg) => {
            const packageData = packages[pkg.id];
            if (!packageData) return null;

            return (
              <Card 
                key={pkg.id}
                className={`bg-gray-900/50 border-white/10 relative overflow-hidden ${
                  pkg.popular ? 'ring-2 ring-green-500/50' : ''
                }`}
                data-testid={`security-package-${pkg.id}`}
              >
                {pkg.popular && (
                  <div className="absolute top-0 right-0 bg-green-500 text-black text-xs font-bold px-3 py-1 rounded-bl-lg">
                    POPULAR
                  </div>
                )}
                <CardHeader className="text-center pb-0">
                  <div className={`w-16 h-16 rounded-full bg-${pkg.color}-500/20 flex items-center justify-center mx-auto mb-4`}>
                    <pkg.icon className={`w-8 h-8 text-${pkg.color}-400`} />
                  </div>
                  <CardTitle className="font-['Outfit']">{packageData.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-white">${packageData.price}</span>
                    <span className="text-gray-500">/audit</span>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <ul className="space-y-3 mb-6">
                    {packageData.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                        <Check className={`w-4 h-4 text-${pkg.color}-400 flex-shrink-0`} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => handlePurchase(pkg.id)}
                    disabled={loading && selectedPackage === pkg.id}
                    className={`w-full ${
                      pkg.popular 
                        ? 'bg-green-500 hover:bg-green-600 text-black' 
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                    data-testid={`purchase-btn-${pkg.id}`}
                  >
                    {loading && selectedPackage === pkg.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        Get Started
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Custom Inquiry */}
        <Card className="bg-gray-900/50 border-white/10 max-w-2xl mx-auto">
          <CardHeader className="text-center">
            <CardTitle className="font-['Outfit']">Need Custom Security?</CardTitle>
            <CardDescription>
              Have specific requirements? Contact us for a tailored security solution.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {showInquiryForm ? (
              <form onSubmit={handleInquirySubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="website_url">Website URL</Label>
                  <Input
                    id="website_url"
                    placeholder="https://yourwebsite.com"
                    value={inquiryData.website_url}
                    onChange={(e) => setInquiryData(prev => ({ ...prev, website_url: e.target.value }))}
                    required
                    className="bg-gray-800/50 border-gray-700"
                    data-testid="inquiry-website-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_email">Contact Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    placeholder="you@example.com"
                    value={inquiryData.contact_email}
                    onChange={(e) => setInquiryData(prev => ({ ...prev, contact_email: e.target.value }))}
                    required
                    className="bg-gray-800/50 border-gray-700"
                    data-testid="inquiry-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Describe Your Needs</Label>
                  <Textarea
                    id="description"
                    placeholder="Tell us about your security requirements..."
                    value={inquiryData.description}
                    onChange={(e) => setInquiryData(prev => ({ ...prev, description: e.target.value }))}
                    required
                    className="bg-gray-800/50 border-gray-700 min-h-[100px]"
                    data-testid="inquiry-description-input"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowInquiryForm(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="inquiry-submit-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Inquiry'}
                  </Button>
                </div>
              </form>
            ) : (
              <div className="text-center">
                <Button
                  onClick={() => setShowInquiryForm(true)}
                  variant="outline"
                  className="border-white/20"
                  data-testid="show-inquiry-form-btn"
                >
                  Request Custom Quote
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
